import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash, WarningCircle, ArrowRight, ShieldCheck, ShieldWarning,
  FilePdf, MagnifyingGlass, Butterfly, Target, ChartBar, ClipboardText,
  BookOpen, CaretDown, CaretRight, ArrowsClockwise, Shield, ChartLineUp, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

/**
 * BowTieModule — Análisis Bow-Tie de riesgos operacionales.
 *
 * Estructura de la imagen guía (5 pasos alrededor del diagrama tipo mariposa):
 *  Step 1: Hazard Identification & Categorization
 *  Step 2: Bow-Tie Diagram Construction
 *  Step 3: Quantitative Analysis (FMEA / RPN / Fuzzy Logic)
 *  Step 4: Risk Prioritization & Mitigation
 *  Step 5: Observation & Documentation
 *
 * Diagrama central:
 *   THREATS/CAUSES → PREVENTIVE BARRIERS (capas) → ESCALATION FACTOR
 *   → TOP EVENT (círculo) + ASSETS (escudo) →
 *   ESCALATION FACTOR → MITIGATIVE BARRIERS (capas) → CONSEQUENCES
 */

const emptyForm = () => ({
  top_event: "",
  hazard: "",
  assets: "",
  escalation_factors: [],
  threats: [],
  consequences: [],
  review_date: "",
});
const addBarrier = "Añadir barrera";

/** Normaliza una barrera. Acepta string legacy o objeto {name, scb, eff, owner, last_review}. */
const normBarrier = (b) => {
  if (typeof b === "string") return { name: b, scb: false, eff: 3, owner: "", last_review: "" };
  return {
    name: b?.name ?? "",
    scb: !!b?.scb,
    eff: Math.max(1, Math.min(5, Number(b?.eff) || 3)),
    owner: b?.owner ?? "",
    last_review: b?.last_review ?? "",
  };
};

/** Convierte barreras a objetos antes de guardar (compat con backend list). */
const barsToObjects = (arr) => (arr || []).map(normBarrier).filter((b) => b.name.trim());

/**
 * Calcula RPN por rama Bow-Tie.
 * base × ∏ max(0.15, 1 − eff/5 × (scb ? 1.3 : 1))
 * Threat base: crítica=25, no crítica=10.  Consequence base: 20.
 */
const calcRpn = (base, barriers) => {
  const bs = (barriers || []).map(normBarrier);
  let f = 1;
  for (const b of bs) {
    const w = b.scb ? 1.3 : 1.0;
    f *= Math.max(0.15, 1 - (b.eff / 5) * w);
  }
  return Math.round(base * f);
};

const rpnThreat = (th) =>
  calcRpn(th?.critical ? 25 : 10, th?.preventive_barriers || []);
const rpnConsequence = (co) => calcRpn(20, co?.reactive_barriers || []);

const rpnTone = (rpn) => {
  if (rpn >= 18) return { label: "ALTO", cls: "bg-red-600 text-white" };
  if (rpn >= 8)  return { label: "MEDIO", cls: "bg-amber-500 text-white" };
  return { label: "BAJO", cls: "bg-emerald-600 text-white" };
};

// ---- Constantes de layout del SVG (mariposa) ----
const SVG_W = 1200;
const SVG_H = 560;
const CENTER_X = SVG_W / 2;
const CENTER_Y = SVG_H / 2;
const TOP_R = 62;                          // radio del "Top Event"
const BARRIER_COLORS_LEFT = ["#0EA5E9", "#0284C7", "#0369A1", "#16A34A"];   // capas preventivas
const BARRIER_COLORS_RIGHT = ["#84CC16", "#22C55E", "#F59E0B", "#F97316"];  // capas mitigativas

export default function BowTieModule() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [showTheory, setShowTheory] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/hse/bowtie").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (it) => {
    setEditing(it.id);
    setForm({
      top_event: it.top_event || "",
      hazard: it.hazard || "",
      assets: it.assets || "",
      escalation_factors: [...(it.escalation_factors || [])],
      threats: (it.threats || []).map((t) => ({
        description: t.description || "",
        preventive_barriers: (t.preventive_barriers || []).map(normBarrier),
        critical: !!t.critical,
      })),
      consequences: (it.consequences || []).map((c) => ({
        description: c.description || "",
        reactive_barriers: (c.reactive_barriers || []).map(normBarrier),
      })),
      review_date: it.review_date || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      // Normaliza barreras a objetos antes de enviar (backend acepta List[Any])
      const payload = {
        ...form,
        threats: form.threats.map((t) => ({
          ...t,
          preventive_barriers: barsToObjects(t.preventive_barriers),
        })),
        consequences: form.consequences.map((c) => ({
          ...c,
          reactive_barriers: barsToObjects(c.reactive_barriers),
        })),
      };
      if (editing) await api.put(`/hse/bowtie/${editing}`, payload);
      else await api.post("/hse/bowtie", payload);
      toast.success(editing ? "Bow-Tie actualizado" : "Bow-Tie creado");
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar Bow-Tie?")) return;
    try { await api.delete(`/hse/bowtie/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const upd = (arr, idx, field, val) => { const n = [...arr]; n[idx] = { ...n[idx], [field]: val }; return n; };

  const selected = useMemo(
    () => items.find((it) => it.id === selectedId) || items[0] || null,
    [items, selectedId],
  );

  return (
    <div className="space-y-6">
      {/* ─── Marco teórico colapsable ─────────────────────────────────── */}
      <TheoryBanner open={showTheory} onToggle={() => setShowTheory((s) => !s)} />

      {/* ─── Auditoría de barreras SCB ──────────────────────────────── */}
      <ScbAuditPanel refreshKey={items.length} />

      {/* ─── 5 pasos metodológicos ───────────────────────────────────── */}
      <StepsRibbon />

      {/* ─── Header: crear/editar ─────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900">{items.length}</span> análisis Bow-Tie registrados
        </p>
        {canWrite && (
          <Dialog
            open={open}
            onOpenChange={(o) => {
              setOpen(o);
              if (!o) { setEditing(null); setForm(emptyForm()); }
            }}
          >
            <DialogTrigger asChild>
              <Button data-testid="bowtie-new-button" className="bg-blue-900 hover:bg-blue-800 gap-2">
                <Plus size={14} weight="bold" /> Nuevo Bow-Tie
              </Button>
            </DialogTrigger>
            <BowTieFormDialog
              form={form}
              setForm={setForm}
              editing={editing}
              upd={upd}
              onSave={save}
            />
          </Dialog>
        )}
      </div>

      {/* ─── Selector de análisis + diagrama SVG ─────────────────────── */}
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-10 bg-white border border-slate-200 rounded-lg">
          Sin análisis Bow-Tie registrados. Crea el primero usando el botón <b>Nuevo Bow-Tie</b>.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap" data-testid="bowtie-selector">
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => setSelectedId(it.id)}
                data-testid={`bowtie-select-${it.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  selected?.id === it.id
                    ? "bg-blue-900 text-white border-blue-950"
                    : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                }`}
              >
                {it.top_event?.slice(0, 40) || "(sin título)"}
              </button>
            ))}
          </div>

          {selected && (
            <BowTieDiagramCard
              item={selected}
              canWrite={canWrite}
              onEdit={() => startEdit(selected)}
              onDelete={() => remove(selected.id)}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * Marco teórico (colapsable)
 * ═══════════════════════════════════════════════════════════════════ */
function TheoryBanner({ open, onToggle }) {
  return (
    <div
      className="bg-gradient-to-r from-blue-950 via-blue-900 to-slate-900 rounded-xl overflow-hidden text-white"
      data-testid="bowtie-theory-banner"
    >
      <button
        onClick={onToggle}
        data-testid="bowtie-theory-toggle"
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/5 transition"
      >
        <div className="flex items-center gap-3">
          <BookOpen size={22} weight="duotone" className="text-amber-400" />
          <div className="text-left">
            <p className="font-heading font-black text-base leading-tight">¿Qué es un Bow-Tie y por qué importa?</p>
            <p className="text-[11px] uppercase tracking-[0.2em] text-blue-200 mt-0.5">
              Marco teórico · barreras · defensas en profundidad
            </p>
          </div>
        </div>
        {open ? <CaretDown size={18} weight="bold" /> : <CaretRight size={18} weight="bold" />}
      </button>

      {open && (
        <div className="px-5 pb-6 pt-2 grid gap-5 lg:grid-cols-3 text-[13px] leading-relaxed">
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-widest font-black text-amber-400">Origen</p>
            <p className="text-blue-100">
              El diagrama Bow-Tie (corbatín) nace en la <b>Universidad de Queensland (1979)</b> y es
              adoptado por <b>Shell</b> tras Piper Alpha (1988). Hoy es referencia obligada de la industria
              minera, petróleo, aviación y nuclear (norma <b>ISO 31000</b>, <b>CCPS</b>, <b>Energy Institute</b>).
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-widest font-black text-amber-400">Anatomía</p>
            <ul className="text-blue-100 space-y-1 list-disc pl-4">
              <li><b>Peligro (Hazard):</b> fuente con potencial de daño (energía, sustancia, situación).</li>
              <li><b>Top Event:</b> pérdida de control sobre el peligro.</li>
              <li><b>Amenazas y consecuencias:</b> causas raíz e impactos.</li>
              <li><b>Barreras:</b> preventivas (izquierda) y mitigativas / reactivas (derecha).</li>
              <li><b>Factores de escalamiento:</b> condiciones que degradan una barrera.</li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-widest font-black text-amber-400">Por qué usarlo</p>
            <ul className="text-blue-100 space-y-1 list-disc pl-4">
              <li>Visualiza el <b>modelo de queso suizo</b> de Reason: capas de defensa y sus huecos.</li>
              <li>Permite auditar la <b>integridad de cada barrera</b> (¿existe? ¿es independiente? ¿la operamos?).</li>
              <li>Base para <b>análisis cuantitativo</b> (FMEA · RPN · lógica difusa) y priorización de mitigaciones.</li>
              <li>Comunicación clara a operadores, supervisores, gerencia y auditores externos.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * Cinta de 5 pasos (ribbon superior)
 * ═══════════════════════════════════════════════════════════════════ */
const STEPS = [
  { n: 1, title: "Identificación y categorización del peligro", icon: MagnifyingGlass, tone: "bg-sky-100 text-sky-900 border-sky-300" },
  { n: 2, title: "Construcción del diagrama Bow-Tie",           icon: Butterfly,       tone: "bg-indigo-100 text-indigo-900 border-indigo-300" },
  { n: 3, title: "Análisis cuantitativo (FMEA · RPN · Fuzzy)",   icon: ChartBar,        tone: "bg-amber-100 text-amber-900 border-amber-300" },
  { n: 4, title: "Priorización y mitigación de riesgos",         icon: Target,          tone: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  { n: 5, title: "Observación y documentación",                  icon: ClipboardText,   tone: "bg-slate-100 text-slate-900 border-slate-300" },
];

function StepsRibbon() {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="bowtie-steps-ribbon">
      <div className="flex items-center gap-2 mb-3">
        <ArrowsClockwise size={16} weight="bold" className="text-blue-900" />
        <p className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-600">
          Ciclo metodológico Bow-Tie · 5 pasos
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {STEPS.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.n}
              data-testid={`bowtie-step-${s.n}`}
              className={`border rounded-lg px-3 py-2.5 flex items-start gap-2 ${s.tone}`}
            >
              <div className="mt-0.5 shrink-0">
                <Icon size={18} weight="duotone" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] font-black tracking-widest opacity-70">STEP {s.n}</p>
                <p className="text-[11px] font-bold leading-snug">{s.title}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * Tarjeta con diagrama SVG (estilo imagen guía) + tablas
 * ═══════════════════════════════════════════════════════════════════ */
function BowTieDiagramCard({ item, canWrite, onEdit, onDelete }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
      data-testid={`bowtie-card-${item.id}`}
    >
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <WarningCircle size={20} weight="fill" className="text-red-600 shrink-0" />
          <p className="font-heading font-black text-slate-900 truncate">{item.top_event}</p>
          {item.hazard && <span className="text-xs text-slate-500 truncate">— {item.hazard}</span>}
        </div>
        <div className="flex items-center gap-3">
          {canWrite && (
            <>
              <button
                data-testid={`bowtie-edit-${item.id}`}
                onClick={onEdit}
                className="text-slate-500 hover:text-blue-800 text-xs font-semibold"
              >
                Editar
              </button>
              <button
                data-testid={`bowtie-pdf-${item.id}`}
                onClick={() =>
                  downloadFile(`/hse/bowtie/${item.id}/pdf`, `bowtie-${(item.top_event || "analysis").slice(0, 30)}.pdf`)
                    .catch((e) => toast.error(apiError(e)))
                }
                className="text-slate-500 hover:text-blue-800 flex items-center gap-1 text-xs font-semibold"
                title="Exportar PDF"
              >
                <FilePdf size={14} weight="duotone" /> PDF
              </button>
              <button
                data-testid={`bowtie-delete-${item.id}`}
                onClick={onDelete}
                className="text-slate-400 hover:text-red-600"
                title="Eliminar"
              >
                <Trash size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-4 overflow-x-auto bg-gradient-to-b from-white to-slate-50">
        <BowTieSVG item={item} />
      </div>

      {/* Tablas resumen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200 border-t border-slate-200">
        <div className="p-4">
          <p className="overline-label mb-2 text-emerald-800">Amenazas → Barreras preventivas</p>
          {(!item.threats || item.threats.length === 0) && (
            <p className="text-xs text-slate-400 italic">Sin amenazas registradas</p>
          )}
          <div className="space-y-2">
            {(item.threats || []).map((th, i) => {
              const rpn = rpnThreat(th);
              const tone = rpnTone(rpn);
              return (
              <div key={i} className="bg-emerald-50 border border-emerald-200 rounded p-2">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-1 flex-wrap">
                  {th.critical && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white font-bold">CRÍTICA</span>
                  )}
                  {th.description}
                  <span className={`ml-auto text-[10px] px-2 py-0.5 rounded font-black ${tone.cls}`} title="Riesgo residual estimado">
                    RPN {rpn} · {tone.label}
                  </span>
                </p>
                <ul className="mt-1 space-y-0.5">
                  {(th.preventive_barriers || []).map((rawB, bi) => {
                    const b = normBarrier(rawB);
                    return (
                    <li key={bi} className="text-xs text-slate-700 flex items-center gap-1.5">
                      <ArrowRight size={10} className="text-emerald-700" />
                      <span>{b.name}</span>
                      {b.scb && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-blue-900 text-white font-bold" title="Safety-Critical Barrier">SCB</span>
                      )}
                      <span className="text-[9px] text-slate-500">Ef. {b.eff}/5</span>
                    </li>
                  );})}
                </ul>
              </div>
            );})}
          </div>
        </div>
        <div className="p-4">
          <p className="overline-label mb-2 text-red-800">Consecuencias → Barreras reactivas</p>
          {(!item.consequences || item.consequences.length === 0) && (
            <p className="text-xs text-slate-400 italic">Sin consecuencias registradas</p>
          )}
          <div className="space-y-2">
            {(item.consequences || []).map((co, i) => {
              const rpn = rpnConsequence(co);
              const tone = rpnTone(rpn);
              return (
              <div key={i} className="bg-red-50 border border-red-200 rounded p-2">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-1 flex-wrap">
                  {co.description}
                  <span className={`ml-auto text-[10px] px-2 py-0.5 rounded font-black ${tone.cls}`} title="Riesgo residual estimado">
                    RPN {rpn} · {tone.label}
                  </span>
                </p>
                <ul className="mt-1 space-y-0.5">
                  {(co.reactive_barriers || []).map((rawB, bi) => {
                    const b = normBarrier(rawB);
                    return (
                    <li key={bi} className="text-xs text-slate-700 flex items-center gap-1.5">
                      <ArrowRight size={10} className="text-red-700" />
                      <span>{b.name}</span>
                      {b.scb && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-blue-900 text-white font-bold" title="Safety-Critical Barrier">SCB</span>
                      )}
                      <span className="text-[9px] text-slate-500">Ef. {b.eff}/5</span>
                    </li>
                  );})}
                </ul>
              </div>
            );})}
          </div>
        </div>
      </div>

      {(item.escalation_factors || []).length > 0 && (
        <div className="px-4 py-3 border-t border-slate-200 bg-amber-50">
          <p className="text-[10px] uppercase tracking-widest font-black text-amber-800 mb-1">
            Factores de escalamiento (degradan barreras)
          </p>
          <div className="flex flex-wrap gap-2">
            {item.escalation_factors.map((f, i) => (
              <span
                key={i}
                className="text-[11px] bg-white border border-amber-300 text-amber-900 rounded-full px-2.5 py-0.5 font-semibold"
              >
                <Target size={10} weight="bold" className="inline mr-1 -mt-0.5" />
                {f}
              </span>
            ))}
          </div>
        </div>
      )}

      <LinkedIncidents bowtieId={item.id} />
      <BowtieHistoryButton bowtieId={item.id} />
    </div>
  );
}

/**
 * Botón + modal para ver la evolución del RPN de un Bow-Tie a lo largo del tiempo.
 * Cada edición del Bow-Tie genera un snapshot automático en el backend.
 */
function BowtieHistoryButton({ bowtieId }) {
  const [open, setOpen] = useState(false);
  const [snaps, setSnaps] = useState(null);

  const load = useCallback(() => {
    api.get(`/hse/bowtie/${bowtieId}/history`)
      .then((r) => setSnaps(r.data || []))
      .catch(() => setSnaps([]));
  }, [bowtieId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <>
      <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center justify-between">
        <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
          <ClockCounterClockwise size={13} weight="duotone" className="text-blue-800" />
          Se guarda un snapshot del RPN cada vez que se edita este Bow-Tie.
        </p>
        <Button
          size="sm"
          variant="outline"
          data-testid={`bowtie-history-btn-${bowtieId}`}
          onClick={() => setOpen(true)}
          className="h-7 text-xs gap-1"
        >
          <ChartLineUp size={12} weight="bold" /> Ver evolución RPN
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ChartLineUp size={20} weight="duotone" className="text-blue-800" />
              Evolución del RPN
            </DialogTitle>
          </DialogHeader>
          {snaps === null ? (
            <p className="text-sm text-slate-400 text-center py-8">Cargando historial…</p>
          ) : snaps.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Aún no hay snapshots. Edita el Bow-Tie para generar el primero.</p>
          ) : (
            <>
              <RpnEvolutionChart snaps={snaps} />
              <table className="w-full text-xs mt-4">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b">
                    <th className="py-1.5">Fecha</th>
                    <th>Acción</th>
                    <th>Editor</th>
                    <th className="text-right">Máx RPN</th>
                    <th className="text-right">Prom.</th>
                    <th>Ramas ALTO</th>
                  </tr>
                </thead>
                <tbody>
                  {snaps.slice().reverse().map((s) => {
                    const high = [...(s.snapshot?.threats || []), ...(s.snapshot?.consequences || [])]
                      .filter((r) => r.rpn >= 18);
                    return (
                      <tr key={s.id} className="border-b border-slate-100" data-testid={`bowtie-history-row-${s.id}`}>
                        <td className="py-1.5">{(s.created_at || "").replace("T", " ").slice(0, 16)}</td>
                        <td>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${
                            s.action === "created" ? "bg-blue-900 text-white" : "bg-amber-500 text-white"
                          }`}>{(s.action || "").toUpperCase()}</span>
                        </td>
                        <td className="text-slate-600 truncate max-w-[140px]">{s.actor || "—"}</td>
                        <td className="text-right font-mono font-bold">{s.snapshot?.max_rpn ?? "—"}</td>
                        <td className="text-right font-mono">{s.snapshot?.avg_rpn ?? "—"}</td>
                        <td className="text-red-700 font-bold">{high.length || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Mini-gráfico SVG con la evolución del `max_rpn` en el tiempo.
 * Requiere >= 1 snapshot; con 1 muestra un solo punto.
 */
function RpnEvolutionChart({ snaps }) {
  const W = 620, H = 180, PAD = 34;
  const points = snaps.map((s) => ({
    t: s.created_at,
    r: Number(s.snapshot?.max_rpn ?? 0),
  }));
  const maxR = Math.max(25, ...points.map((p) => p.r));
  const step = points.length > 1 ? (W - PAD * 2) / (points.length - 1) : 0;
  const yFor = (r) => H - PAD - (r / maxR) * (H - PAD * 2);
  const xFor = (i) => PAD + i * step;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto bg-slate-50 rounded border border-slate-200" data-testid="bowtie-rpn-chart">
      {/* zonas de riesgo */}
      <rect x={PAD} y={yFor(maxR)} width={W - PAD * 2} height={yFor(18) - yFor(maxR)} fill="#FECACA" opacity="0.6" />
      <rect x={PAD} y={yFor(18)} width={W - PAD * 2} height={yFor(8) - yFor(18)} fill="#FDE68A" opacity="0.6" />
      <rect x={PAD} y={yFor(8)} width={W - PAD * 2} height={yFor(0) - yFor(8)} fill="#BBF7D0" opacity="0.5" />
      {/* eje Y ticks */}
      {[0, 8, 18, maxR].map((r, i) => (
        <g key={i}>
          <line x1={PAD} y1={yFor(r)} x2={W - PAD} y2={yFor(r)} stroke="#CBD5E1" strokeWidth="0.5" strokeDasharray="2 3" />
          <text x={PAD - 5} y={yFor(r) + 3} textAnchor="end" fontSize="9" fill="#64748B">{r}</text>
        </g>
      ))}
      {/* polyline */}
      {points.length > 1 && (
        <polyline
          fill="none"
          stroke="#1E3A8A"
          strokeWidth="2"
          points={points.map((p, i) => `${xFor(i)},${yFor(p.r)}`).join(" ")}
        />
      )}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={xFor(i)} cy={yFor(p.r)} r="4" fill={p.r >= 18 ? "#DC2626" : p.r >= 8 ? "#F59E0B" : "#16A34A"} stroke="white" strokeWidth="1.5" />
          <text x={xFor(i)} y={yFor(p.r) - 8} textAnchor="middle" fontSize="9" fontWeight="700" fill="#0F172A">{p.r}</text>
        </g>
      ))}
      <text x={PAD} y={H - 4} fontSize="9" fill="#94A3B8">
        {points[0]?.t?.slice(0, 10) || "—"}
      </text>
      <text x={W - PAD} y={H - 4} textAnchor="end" fontSize="9" fill="#94A3B8">
        {points[points.length - 1]?.t?.slice(0, 10) || "—"}
      </text>
    </svg>
  );
}

/**
 * Panel de auditoría de barreras Safety-Critical (SCB).
 * Consume /hse/bowtie/audit-scb con semáforo verde/ámbar/rojo por barrera.
 */
function ScbAuditPanel({ refreshKey }) {
  const [audit, setAudit] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [interval, setInt] = useState(30);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!expanded) return;
    api.get(`/hse/bowtie/audit-scb`, { params: { interval_days: interval } })
      .then((r) => setAudit(r.data))
      .catch(() => setAudit({ counters: { verde: 0, ambar: 0, rojo: 0 }, items: [], total: 0 }));
  }, [expanded, interval, refreshKey]);

  const rows = audit?.items?.filter((r) => filter === "all" || r.status === filter) || [];
  const statusTone = {
    verde: "bg-emerald-500 text-white",
    ambar: "bg-amber-500 text-white",
    rojo:  "bg-red-600 text-white",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="bowtie-scb-audit">
      <button
        onClick={() => setExpanded((s) => !s)}
        data-testid="bowtie-scb-toggle"
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-3">
          <Shield size={20} weight="duotone" className="text-blue-800" />
          <div className="text-left">
            <p className="font-heading font-black text-base leading-tight text-slate-900">
              Auditoría mensual de barreras Safety-Critical (SCB)
            </p>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-0.5">
              Semáforo por barrera · dueño responsable · frecuencia de verificación
            </p>
          </div>
        </div>
        {audit && (
          <div className="hidden sm:flex items-center gap-1.5 mr-3">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white font-bold">V {audit.counters.verde}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold">A {audit.counters.ambar}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-600 text-white font-bold">R {audit.counters.rojo}</span>
          </div>
        )}
        {expanded ? <CaretDown size={18} weight="bold" /> : <CaretRight size={18} weight="bold" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 pt-1">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <label className="text-[11px] text-slate-600 flex items-center gap-1.5">
              Intervalo (días):
              <select
                data-testid="scb-interval-select"
                value={interval}
                onChange={(e) => setInt(Number(e.target.value))}
                className="h-7 text-xs border border-slate-200 rounded px-1.5 bg-white"
              >
                <option value={7}>7</option>
                <option value={15}>15</option>
                <option value={30}>30 (mensual)</option>
                <option value={60}>60</option>
                <option value={90}>90</option>
              </select>
            </label>
            <div className="flex items-center gap-1">
              {["all", "verde", "ambar", "rojo"].map((k) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  data-testid={`scb-filter-${k}`}
                  className={`text-[10px] font-bold px-2 py-1 rounded ${
                    filter === k
                      ? (k === "all" ? "bg-slate-900 text-white" : statusTone[k])
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {k.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {audit && rows.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-6 text-center">
              {audit.total === 0
                ? "No hay barreras Safety-Critical marcadas. Marca la casilla \"SCB\" al editar un Bow-Tie."
                : "No hay barreras en este estado con este intervalo."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="scb-audit-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b">
                    <th className="py-1.5 pr-2">Status</th>
                    <th className="pr-2">Barrera SCB</th>
                    <th className="pr-2">Bow-Tie</th>
                    <th className="pr-2">Rama</th>
                    <th className="pr-2">Dueño</th>
                    <th className="pr-2 text-right">Ef.</th>
                    <th className="pr-2">Última revisión</th>
                    <th className="text-right">Días</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`scb-row-${i}`}>
                      <td className="py-1.5 pr-2">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-black uppercase ${statusTone[r.status]}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="pr-2 font-bold text-slate-800">{r.barrier}</td>
                      <td className="pr-2 text-slate-600 truncate max-w-[160px]">{r.top_event}</td>
                      <td className="pr-2 text-slate-500 truncate max-w-[140px]" title={r.branch_desc}>
                        {r.branch === "threat" ? "🔺" : "🔻"} {r.branch_desc}
                      </td>
                      <td className="pr-2 text-slate-700">{r.owner}</td>
                      <td className="pr-2 text-right font-mono">{r.eff}/5</td>
                      <td className="pr-2 text-slate-500">{r.last_review || "—"}</td>
                      <td className="text-right font-mono font-bold">{r.days_since_review ?? "∞"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Muestra investigaciones/incidentes reales vinculados a este Bow-Tie.
 * Endpoint: GET /hse/bowtie/{id}/incidents
 */
function LinkedIncidents({ bowtieId }) {
  const [incidents, setIncidents] = useState(null);
  useEffect(() => {
    if (!bowtieId) return;
    api.get(`/hse/bowtie/${bowtieId}/incidents`)
      .then((r) => setIncidents(r.data))
      .catch(() => setIncidents([]));
  }, [bowtieId]);
  if (!incidents) return null;
  return (
    <div className="px-4 py-3 border-t border-slate-200 bg-slate-50" data-testid={`bowtie-incidents-${bowtieId}`}>
      <p className="text-[10px] uppercase tracking-widest font-black text-slate-700 mb-2">
        <WarningCircle size={11} weight="fill" className="inline text-red-600 mr-1 -mt-0.5" />
        Incidentes vinculados ({incidents.length})
      </p>
      {incidents.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic">
          Ningún incidente/investigación registrado. Los investigadores pueden vincular incidentes reales a este Bow-Tie desde el módulo <b>Investigaciones</b>.
        </p>
      ) : (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {incidents.map((inv) => (
            <div key={inv.id} className="bg-white border border-slate-200 rounded p-2 text-xs" data-testid={`bowtie-incident-${inv.id}`}>
              <div className="flex items-center gap-1.5">
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${
                  inv.status === "closed" ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"
                }`}>{(inv.status || "open").toUpperCase()}</span>
                <span className="font-bold text-slate-800 truncate">{inv.title || "(sin título)"}</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {inv.date || "—"} · {(inv.investigator || "sin investigador").slice(0, 40)}
              </p>
              {inv.description && (
                <p className="text-[10px] text-slate-600 mt-1 line-clamp-2">{inv.description}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════
 * SVG del diagrama Bow-Tie (embudos escalonados)
 * ═══════════════════════════════════════════════════════════════════ */
function BowTieSVG({ item }) {
  const threats = (item.threats || []).slice(0, 6);
  const consequences = (item.consequences || []).slice(0, 6);
  const nT = Math.max(1, threats.length);
  const nC = Math.max(1, consequences.length);

  // Alturas del embudo
  const funnelLeftFarWidth = SVG_H - 100;
  const funnelLeftNearWidth = 180;
  const funnelRightFarWidth = SVG_H - 100;
  const funnelRightNearWidth = 180;

  const leftBoxX = 40;
  const leftBoxW = 190;
  const rightBoxX = SVG_W - 40 - 190;
  const rightBoxW = 190;

  // 4 capas de barreras (izquierda: barreras preventivas)
  const leftBarriers = [
    { x: leftBoxX + leftBoxW + 40, farW: funnelLeftFarWidth * 0.92, nearW: funnelLeftNearWidth * 1.35 },
    { x: leftBoxX + leftBoxW + 120, farW: funnelLeftFarWidth * 0.72, nearW: funnelLeftNearWidth * 1.15 },
    { x: leftBoxX + leftBoxW + 200, farW: funnelLeftFarWidth * 0.52, nearW: funnelLeftNearWidth * 0.95 },
    { x: leftBoxX + leftBoxW + 280, farW: funnelLeftFarWidth * 0.34, nearW: funnelLeftNearWidth * 0.8 },
  ];

  const rightBarriers = [
    { x: rightBoxX - 40, farW: funnelRightFarWidth * 0.92, nearW: funnelRightNearWidth * 1.35 },
    { x: rightBoxX - 120, farW: funnelRightFarWidth * 0.72, nearW: funnelRightNearWidth * 1.15 },
    { x: rightBoxX - 200, farW: funnelRightFarWidth * 0.52, nearW: funnelRightNearWidth * 0.95 },
    { x: rightBoxX - 280, farW: funnelRightFarWidth * 0.34, nearW: funnelRightNearWidth * 0.8 },
  ];

  // Posiciones y de threats (fila vertical uniforme)
  const leftYs = Array.from({ length: nT }, (_, i) => {
    const gap = (SVG_H - 100) / nT;
    return 50 + gap * i + gap / 2;
  });
  const rightYs = Array.from({ length: nC }, (_, i) => {
    const gap = (SVG_H - 100) / nC;
    return 50 + gap * i + gap / 2;
  });

  return (
    <svg
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      className="w-full h-auto min-w-[900px]"
      style={{ maxHeight: 620 }}
      data-testid="bowtie-svg"
    >
      {/* Fondos sutil izquierdo (azul) y derecho (amarillo) */}
      <defs>
        <linearGradient id="bg-left" x1="0" x2="1">
          <stop offset="0" stopColor="#DBEAFE" stopOpacity="0.6" />
          <stop offset="1" stopColor="#EFF6FF" stopOpacity="0.15" />
        </linearGradient>
        <linearGradient id="bg-right" x1="1" x2="0">
          <stop offset="0" stopColor="#FEF3C7" stopOpacity="0.55" />
          <stop offset="1" stopColor="#FFFBEB" stopOpacity="0.15" />
        </linearGradient>
        <filter id="soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.4" floodOpacity="0.18" />
        </filter>
      </defs>

      {/* Trapecio de fondo (embudo izquierdo) */}
      <polygon
        points={`${leftBoxX + leftBoxW},50 ${CENTER_X - TOP_R - 10},${CENTER_Y - funnelLeftNearWidth / 2} ${CENTER_X - TOP_R - 10},${CENTER_Y + funnelLeftNearWidth / 2} ${leftBoxX + leftBoxW},${SVG_H - 50}`}
        fill="url(#bg-left)"
      />
      {/* Trapecio de fondo (embudo derecho) */}
      <polygon
        points={`${CENTER_X + TOP_R + 10},${CENTER_Y - funnelRightNearWidth / 2} ${rightBoxX},50 ${rightBoxX},${SVG_H - 50} ${CENTER_X + TOP_R + 10},${CENTER_Y + funnelRightNearWidth / 2}`}
        fill="url(#bg-right)"
      />

      {/* Etiquetas grandes de sección */}
      <text x={leftBoxX + leftBoxW / 2} y={30} textAnchor="middle" className="fill-slate-700"
            style={{ fontSize: 13, fontWeight: 900, letterSpacing: 1 }}>
        AMENAZAS · CAUSAS
      </text>
      <text x={leftBoxX + leftBoxW + 160} y={30} textAnchor="middle" className="fill-slate-700"
            style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.8 }}>
        BARRERAS PREVENTIVAS
      </text>
      <text x={rightBoxX - 160} y={30} textAnchor="middle" className="fill-slate-700"
            style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.8 }}>
        BARRERAS MITIGATIVAS / REACTIVAS
      </text>
      <text x={rightBoxX + rightBoxW / 2} y={30} textAnchor="middle" className="fill-slate-700"
            style={{ fontSize: 13, fontWeight: 900, letterSpacing: 1 }}>
        CONSECUENCIAS
      </text>

      {/* Barreras preventivas (4 capas trapezoidales de fondo) */}
      {leftBarriers.map((b, i) => {
        const farY1 = 50 + (SVG_H - 100 - b.farW) / 2;
        const farY2 = farY1 + b.farW;
        const nearY1 = CENTER_Y - b.nearW / 2;
        const nearY2 = CENTER_Y + b.nearW / 2;
        return (
          <polygon
            key={`lb-${i}`}
            points={`${b.x},${farY1} ${b.x + 60},${nearY1} ${b.x + 60},${nearY2} ${b.x},${farY2}`}
            fill={BARRIER_COLORS_LEFT[i]}
            fillOpacity="0.55"
            stroke="#0F172A"
            strokeOpacity="0.15"
          />
        );
      })}

      {/* Barreras mitigativas (4 capas trapezoidales, invertidas) */}
      {rightBarriers.map((b, i) => {
        const farY1 = 50 + (SVG_H - 100 - b.farW) / 2;
        const farY2 = farY1 + b.farW;
        const nearY1 = CENTER_Y - b.nearW / 2;
        const nearY2 = CENTER_Y + b.nearW / 2;
        return (
          <polygon
            key={`rb-${i}`}
            points={`${b.x},${nearY1} ${b.x + 60},${farY1} ${b.x + 60},${farY2} ${b.x},${nearY2}`}
            fill={BARRIER_COLORS_RIGHT[i]}
            fillOpacity="0.55"
            stroke="#0F172A"
            strokeOpacity="0.15"
          />
        );
      })}

      {/* Escalation factor (arriba y abajo del centro) */}
      <EscalationMarker x={CENTER_X - 105} y={CENTER_Y - 130} label="Escalation Factor" />
      <EscalationMarker x={CENTER_X - 105} y={CENTER_Y + 100} label="Escalation Factor" />

      {/* Círculo del TOP EVENT — muestra el evento real */}
      <circle cx={CENTER_X} cy={CENTER_Y} r={TOP_R + 8} fill="#FFFFFF" stroke="#1E3A8A" strokeWidth="3" filter="url(#soft-shadow)" />
      <circle cx={CENTER_X} cy={CENTER_Y} r={TOP_R} fill="#DC2626" stroke="#991B1B" strokeWidth="2" />
      {wrapText(item.top_event || "Top event", 14).map((line, i, arr) => {
        const dy = 4 - ((arr.length - 1) * 6) + i * 12;
        return (
          <text
            key={`tev-${i}`}
            x={CENTER_X}
            y={CENTER_Y + dy}
            textAnchor="middle"
            className="fill-white"
            style={{ fontSize: 12, fontWeight: 900 }}
          >
            {line}
          </text>
        );
      })}

      {/* Escudo de Assets debajo del círculo */}
      <g transform={`translate(${CENTER_X - 22}, ${CENTER_Y + TOP_R + 30})`}>
        <path
          d="M22 0 L44 8 L44 26 C44 36 34 46 22 52 C10 46 0 36 0 26 L0 8 Z"
          fill="#1E3A8A"
          stroke="#0F172A"
          strokeOpacity="0.3"
        />
        <text x={22} y={30} textAnchor="middle" className="fill-white"
              style={{ fontSize: 8, fontWeight: 900, letterSpacing: 0.5 }}>
          ASSETS
        </text>
      </g>
      <text x={CENTER_X} y={CENTER_Y + TOP_R + 100} textAnchor="middle" className="fill-slate-700"
            style={{ fontSize: 10, fontWeight: 700 }}>
        {(item.assets || "Activos protegidos").slice(0, 46)}
      </text>

      {/* Threats + sus barreras preventivas alineadas con cada capa */}
      {leftYs.map((y, i) => {
        const th = threats[i];
        const label = th?.description || `Amenaza ${i + 1}`;
        const isCritical = !!th?.critical;
        const barriers = (th?.preventive_barriers || []).slice(0, 4);
        return (
          <g key={`t-${i}`}>
            <line x1={leftBoxX + leftBoxW} y1={y} x2={CENTER_X - TOP_R - 10} y2={y} stroke="#64748B" strokeWidth="1.2" strokeDasharray="3 3" />
            <rect
              x={leftBoxX}
              y={y - 15}
              width={leftBoxW}
              height={30}
              rx={4}
              fill="#FFFFFF"
              stroke={isCritical ? "#DC2626" : "#CBD5E1"}
              strokeWidth={isCritical ? "1.8" : "1"}
              filter="url(#soft-shadow)"
            />
            <text x={leftBoxX + 10} y={y + 4} className="fill-slate-800"
                  style={{ fontSize: 11, fontWeight: 700 }}>
              {truncate(label, 26)}
            </text>
            {isCritical && (
              <>
                <rect x={leftBoxX + leftBoxW - 22} y={y - 8} width="18" height="16" rx="2" fill="#DC2626" />
                <text x={leftBoxX + leftBoxW - 13} y={y + 4} textAnchor="middle" className="fill-white"
                      style={{ fontSize: 9, fontWeight: 900 }}>!</text>
              </>
            )}
            {/* Píldoras de barrera sobre cada capa */}
            {barriers.map((b, bi) => {
              const cx = leftBarriers[bi].x + 30;
              return (
                <g key={`tb-${i}-${bi}`}>
                  <rect
                    x={cx - 55}
                    y={y - 10}
                    width={110}
                    height={20}
                    rx={10}
                    fill="#FFFFFF"
                    stroke={BARRIER_COLORS_LEFT[bi]}
                    strokeWidth="1.4"
                    filter="url(#soft-shadow)"
                  />
                  <text x={cx} y={y + 4} textAnchor="middle" className="fill-slate-800"
                        style={{ fontSize: 9.5, fontWeight: 700 }}>
                    {truncate(normBarrier(b).name, 16)}
                  </text>
                  {normBarrier(b).scb && (
                    <text x={cx + 42} y={y - 12} textAnchor="middle" className="fill-blue-900"
                          style={{ fontSize: 7, fontWeight: 900 }}>SCB</text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}

      {/* Consequences + sus barreras reactivas alineadas con cada capa */}
      {rightYs.map((y, i) => {
        const co = consequences[i];
        const label = co?.description || `Consecuencia ${i + 1}`;
        const barriers = (co?.reactive_barriers || []).slice(0, 4);
        return (
          <g key={`c-${i}`}>
            <line x1={CENTER_X + TOP_R + 10} y1={y} x2={rightBoxX} y2={y} stroke="#64748B" strokeWidth="1.2" strokeDasharray="3 3" />
            <rect
              x={rightBoxX}
              y={y - 15}
              width={rightBoxW}
              height={30}
              rx={4}
              fill="#DC2626"
              stroke="#7F1D1D"
              strokeWidth="1.2"
              filter="url(#soft-shadow)"
            />
            <text x={rightBoxX + rightBoxW / 2} y={y + 4} textAnchor="middle" className="fill-white"
                  style={{ fontSize: 11, fontWeight: 800 }}>
              {truncate(label, 24)}
            </text>
            {/* Píldoras de barreras reactivas sobre cada capa (capa 0 = más cercana al centro) */}
            {barriers.map((b, bi) => {
              const cx = rightBarriers[bi].x + 30;
              return (
                <g key={`cb-${i}-${bi}`}>
                  <rect
                    x={cx - 55}
                    y={y - 10}
                    width={110}
                    height={20}
                    rx={10}
                    fill="#FFFFFF"
                    stroke={BARRIER_COLORS_RIGHT[bi]}
                    strokeWidth="1.4"
                    filter="url(#soft-shadow)"
                  />
                  <text x={cx} y={y + 4} textAnchor="middle" className="fill-slate-800"
                        style={{ fontSize: 9.5, fontWeight: 700 }}>
                    {truncate(normBarrier(b).name, 16)}
                  </text>
                  {normBarrier(b).scb && (
                    <text x={cx + 42} y={y - 12} textAnchor="middle" className="fill-blue-900"
                          style={{ fontSize: 7, fontWeight: 900 }}>SCB</text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}

      {/* Leyenda de flujo */}
      <text x={CENTER_X} y={SVG_H - 12} textAnchor="middle" className="fill-slate-400"
            style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>
        HAZARD → THREATS → PREVENTIVE BARRIERS → TOP EVENT → MITIGATIVE BARRIERS → CONSEQUENCES
      </text>
    </svg>
  );
}

function EscalationMarker({ x, y, label }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <circle cx={0} cy={0} r={9} fill="#F59E0B" stroke="#78350F" strokeWidth="1.4" />
      <circle cx={0} cy={0} r={3} fill="#78350F" />
      <text x={16} y={4} className="fill-amber-800" style={{ fontSize: 10, fontWeight: 800 }}>
        {label}
      </text>
    </g>
  );
}

function truncate(t, n) {
  if (!t) return "";
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}

/** Envuelve `text` en líneas de ~maxChars caracteres respetando palabras. */
function wrapText(text, maxChars) {
  const words = String(text || "").trim().split(/\s+/);
  if (words.length === 0 || !words[0]) return [""];
  const lines = [];
  let cur = "";
  for (const w of words) {
    if (!cur) { cur = w; continue; }
    if ((cur + " " + w).length <= maxChars) cur = cur + " " + w;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines.slice(0, 3);
}

/* ═════════════════════════════════════════════════════════════════════
 * Diálogo de formulario (crear / editar) — reutiliza el layout previo
 * ═══════════════════════════════════════════════════════════════════ */
function BowTieFormDialog({ form, setForm, editing, upd, onSave }) {
  const addEsc = () => setForm({ ...form, escalation_factors: [...form.escalation_factors, ""] });
  const setEsc = (i, v) => {
    const n = [...form.escalation_factors]; n[i] = v; setForm({ ...form, escalation_factors: n });
  };
  const delEsc = (i) => setForm({ ...form, escalation_factors: form.escalation_factors.filter((_, x) => x !== i) });

  return (
    <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">{editing ? "Editar" : "Nuevo"} Bow-Tie</DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="overline-label">Evento no deseado (top event)</Label>
            <Input
              data-testid="bowtie-top-event"
              value={form.top_event}
              onChange={(e) => setForm({ ...form, top_event: e.target.value })}
              placeholder="Ej. Derrumbe en labor"
            />
          </div>
          <div className="space-y-1">
            <Label className="overline-label">Peligro asociado</Label>
            <Input
              data-testid="bowtie-hazard"
              value={form.hazard}
              onChange={(e) => setForm({ ...form, hazard: e.target.value })}
              placeholder="Ej. Colapso de terreno"
            />
          </div>
          <div className="space-y-1">
            <Label className="overline-label">Activos protegidos (assets)</Label>
            <Input
              data-testid="bowtie-assets"
              value={form.assets}
              onChange={(e) => setForm({ ...form, assets: e.target.value })}
              placeholder="Ej. Personas, equipos, producción"
            />
          </div>
          <div className="space-y-1">
            <Label className="overline-label">Fecha de revisión</Label>
            <Input
              type="date"
              data-testid="bowtie-review-date"
              value={form.review_date || ""}
              onChange={(e) => setForm({ ...form, review_date: e.target.value })}
            />
          </div>
        </div>

        {/* Escalation factors */}
        <div className="border border-amber-200 rounded-lg p-3 bg-amber-50/50">
          <div className="flex items-center justify-between mb-2">
            <p className="font-heading font-bold text-sm text-amber-900 flex items-center gap-1">
              <Target size={14} weight="fill" className="text-amber-700" /> Factores de escalamiento
            </p>
            <Button
              size="sm"
              variant="ghost"
              data-testid="bowtie-add-escalation"
              onClick={addEsc}
              className="h-7 text-xs text-amber-900"
            >
              <Plus size={12} weight="bold" /> Factor
            </Button>
          </div>
          {form.escalation_factors.length === 0 && (
            <p className="text-[11px] text-slate-400 italic">
              Condiciones que degradan las barreras (fatiga, exceso de horas, mala visibilidad, falta de mantenimiento…)
            </p>
          )}
          <div className="space-y-1">
            {form.escalation_factors.map((f, i) => (
              <div key={i} className="flex items-center gap-1">
                <Input
                  className="h-7 text-xs"
                  value={f}
                  onChange={(e) => setEsc(i, e.target.value)}
                  data-testid={`bowtie-escalation-${i}`}
                />
                <button onClick={() => delEsc(i)} className="text-slate-400 hover:text-red-600">
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Amenazas */}
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="font-heading font-bold text-sm text-slate-900 flex items-center gap-1">
              <ShieldCheck size={14} className="text-emerald-700" /> Amenazas (con barreras preventivas)
            </p>
            <Button
              size="sm"
              variant="ghost"
              data-testid="bowtie-add-threat"
              onClick={() =>
                setForm({
                  ...form,
                  threats: [...form.threats, { description: "", preventive_barriers: [], critical: false }],
                })
              }
              className="h-7 text-xs text-blue-900"
            >
              <Plus size={12} weight="bold" /> Amenaza
            </Button>
          </div>
          <div className="space-y-3">
            {form.threats.map((th, idx) => (
              <div key={idx} className="bg-emerald-50 border border-emerald-200 rounded p-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    className="h-8 text-sm"
                    placeholder="Amenaza / causa"
                    value={th.description}
                    onChange={(e) => setForm({ ...form, threats: upd(form.threats, idx, "description", e.target.value) })}
                    data-testid={`bowtie-threat-desc-${idx}`}
                  />
                  <label className="flex items-center gap-1 text-[11px] text-slate-600 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={th.critical}
                      onChange={(e) => setForm({ ...form, threats: upd(form.threats, idx, "critical", e.target.checked) })}
                    />{" "}
                    Crítica
                  </label>
                  <button
                    onClick={() => setForm({ ...form, threats: form.threats.filter((_, i) => i !== idx) })}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <Trash size={14} />
                  </button>
                </div>
                <div className="pl-4">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-[11px] uppercase tracking-widest font-bold text-emerald-800">
                      Barreras preventivas
                    </p>
                    <button
                      className="text-[11px] text-blue-900 hover:underline"
                      onClick={() =>
                        setForm({
                          ...form,
                          threats: upd(form.threats, idx, "preventive_barriers", [...th.preventive_barriers, ""]),
                        })
                      }
                    >
                      + {addBarrier}
                    </button>
                  </div>
                  {th.preventive_barriers.map((rawB, bi) => {
                    const b = normBarrier(rawB);
                    const updBarrier = (patch) => {
                      const nb = [...th.preventive_barriers];
                      nb[bi] = { ...b, ...patch };
                      setForm({ ...form, threats: upd(form.threats, idx, "preventive_barriers", nb) });
                    };
                    return (
                    <div key={bi} className="bg-white border border-emerald-200 rounded p-1.5 mb-1.5 space-y-1">
                      <div className="flex items-center gap-1">
                        <Shield size={12} className="text-emerald-700 shrink-0" />
                        <Input
                          className="h-7 text-xs flex-1"
                          placeholder="Nombre de la barrera preventiva"
                          value={b.name}
                          onChange={(e) => updBarrier({ name: e.target.value })}
                        />
                        <button
                          onClick={() =>
                            setForm({
                              ...form,
                              threats: upd(
                                form.threats,
                                idx,
                                "preventive_barriers",
                                th.preventive_barriers.filter((_, i) => i !== bi),
                              ),
                            })
                          }
                          className="text-slate-400 hover:text-red-600"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                      <div className="flex items-center gap-3 pl-4 text-[11px] text-slate-600 flex-wrap">
                        <label className="flex items-center gap-1 cursor-pointer" title="Safety-Critical Barrier (barrera crítica de seguridad)">
                          <input
                            type="checkbox"
                            checked={b.scb}
                            onChange={(e) => updBarrier({ scb: e.target.checked })}
                          />
                          <span className={b.scb ? "font-bold text-blue-900" : ""}>SCB</span>
                        </label>
                        <label className="flex items-center gap-1.5">
                          Efectividad
                          <input
                            type="range"
                            min="1"
                            max="5"
                            step="1"
                            value={b.eff}
                            onChange={(e) => updBarrier({ eff: Number(e.target.value) })}
                            className="w-24 accent-blue-900"
                            data-testid={`bowtie-threat-${idx}-barrier-${bi}-eff`}
                          />
                          <span className="font-mono font-bold text-blue-900 w-6 text-center">{b.eff}/5</span>
                        </label>
                        {b.scb && (
                          <>
                            <Input
                              className="h-6 text-[11px] flex-1 min-w-[120px]"
                              placeholder="Dueño (responsable)"
                              value={b.owner}
                              onChange={(e) => updBarrier({ owner: e.target.value })}
                              data-testid={`bowtie-threat-${idx}-barrier-${bi}-owner`}
                            />
                            <Input
                              type="date"
                              className="h-6 text-[11px]"
                              value={b.last_review || ""}
                              onChange={(e) => updBarrier({ last_review: e.target.value })}
                              data-testid={`bowtie-threat-${idx}-barrier-${bi}-review`}
                              title="Última revisión"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );})}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Consecuencias */}
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="font-heading font-bold text-sm text-slate-900 flex items-center gap-1">
              <ShieldWarning size={14} className="text-red-700" /> Consecuencias (con barreras reactivas)
            </p>
            <Button
              size="sm"
              variant="ghost"
              data-testid="bowtie-add-consequence"
              onClick={() =>
                setForm({
                  ...form,
                  consequences: [...form.consequences, { description: "", reactive_barriers: [] }],
                })
              }
              className="h-7 text-xs text-blue-900"
            >
              <Plus size={12} weight="bold" /> Consecuencia
            </Button>
          </div>
          <div className="space-y-3">
            {form.consequences.map((co, idx) => (
              <div key={idx} className="bg-red-50 border border-red-200 rounded p-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    className="h-8 text-sm"
                    placeholder="Consecuencia potencial"
                    value={co.description}
                    onChange={(e) => setForm({ ...form, consequences: upd(form.consequences, idx, "description", e.target.value) })}
                    data-testid={`bowtie-cons-desc-${idx}`}
                  />
                  <button
                    onClick={() => setForm({ ...form, consequences: form.consequences.filter((_, i) => i !== idx) })}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <Trash size={14} />
                  </button>
                </div>
                <div className="pl-4">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-[11px] uppercase tracking-widest font-bold text-red-800">
                      Barreras reactivas / mitigadoras
                    </p>
                    <button
                      className="text-[11px] text-blue-900 hover:underline"
                      onClick={() =>
                        setForm({
                          ...form,
                          consequences: upd(form.consequences, idx, "reactive_barriers", [...co.reactive_barriers, ""]),
                        })
                      }
                    >
                      + {addBarrier}
                    </button>
                  </div>
                  {co.reactive_barriers.map((rawB, bi) => {
                    const b = normBarrier(rawB);
                    const updBarrier = (patch) => {
                      const nb = [...co.reactive_barriers];
                      nb[bi] = { ...b, ...patch };
                      setForm({ ...form, consequences: upd(form.consequences, idx, "reactive_barriers", nb) });
                    };
                    return (
                    <div key={bi} className="bg-white border border-red-200 rounded p-1.5 mb-1.5 space-y-1">
                      <div className="flex items-center gap-1">
                        <ShieldWarning size={12} className="text-red-700 shrink-0" />
                        <Input
                          className="h-7 text-xs flex-1"
                          placeholder="Nombre de la barrera reactiva"
                          value={b.name}
                          onChange={(e) => updBarrier({ name: e.target.value })}
                        />
                        <button
                          onClick={() =>
                            setForm({
                              ...form,
                              consequences: upd(
                                form.consequences,
                                idx,
                                "reactive_barriers",
                                co.reactive_barriers.filter((_, i) => i !== bi),
                              ),
                            })
                          }
                          className="text-slate-400 hover:text-red-600"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                      <div className="flex items-center gap-3 pl-4 text-[11px] text-slate-600 flex-wrap">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={b.scb}
                            onChange={(e) => updBarrier({ scb: e.target.checked })}
                          />
                          <span className={b.scb ? "font-bold text-blue-900" : ""}>SCB</span>
                        </label>
                        <label className="flex items-center gap-1.5">
                          Efectividad
                          <input
                            type="range"
                            min="1"
                            max="5"
                            step="1"
                            value={b.eff}
                            onChange={(e) => updBarrier({ eff: Number(e.target.value) })}
                            className="w-24 accent-blue-900"
                            data-testid={`bowtie-cons-${idx}-barrier-${bi}-eff`}
                          />
                          <span className="font-mono font-bold text-blue-900 w-6 text-center">{b.eff}/5</span>
                        </label>
                        {b.scb && (
                          <>
                            <Input
                              className="h-6 text-[11px] flex-1 min-w-[120px]"
                              placeholder="Dueño (responsable)"
                              value={b.owner}
                              onChange={(e) => updBarrier({ owner: e.target.value })}
                              data-testid={`bowtie-cons-${idx}-barrier-${bi}-owner`}
                            />
                            <Input
                              type="date"
                              className="h-6 text-[11px]"
                              value={b.last_review || ""}
                              onChange={(e) => updBarrier({ last_review: e.target.value })}
                              data-testid={`bowtie-cons-${idx}-barrier-${bi}-review`}
                              title="Última revisión"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );})}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Button
        data-testid="bowtie-save"
        onClick={onSave}
        disabled={!form.top_event}
        className="bg-blue-900 hover:bg-blue-800 w-full"
      >
        {editing ? "Actualizar" : "Crear"}
      </Button>
    </DialogContent>
  );
}
